import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridServerTable } from 'core/components';
import { SortDirection } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { allColumnDefs } from 'quotes/components/CustomerQuoteList/Table/Columns';
import { QuoteEntityType } from 'quotes/enums';
import { quoteService } from 'quotes/services';
import { Level, Variant } from 'theme/enums';

const PAGE_SIZE = 10;
const CARD_HEIGHT = 480;

const defaultColDef = {
  enableRowGroup: false,
  filter: false,
  floatingFilter: false,
  sortable: false,
  suppressHeaderFilterButton: true,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
};

interface IProps {
  corporateUserIds?: number[];
  type?: QuoteEntityType;
}

export const RecentCustomerQuotesTable = ({
  corporateUserIds = [],
  type = QuoteEntityType.QUOTE,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const isOrder = type === QuoteEntityType.ORDER;

  const tableId = isOrder ? 'recentCustomerOrdersTable' : 'recentCustomerQuotesTable';
  const titleKey = isOrder ? 'titleRecentCustomerOrders' : 'titleRecentCustomerQuotes';

  const columnDefs = useMemo(() => {
    const cols = allColumnDefs(theme, type);

    return [
      isOrder ? cols.orderIdColumn : cols.quoteIdColumn,
      { ...cols.customerColumn, flex: 1 },
      { ...cols.contactColumn, flex: 1 },
      { ...(isOrder ? cols.externalOrderNumberColumn : cols.jobColumn), flex: 1 },
      cols.statusColumn,
    ] as ColDef[];
  }, [isOrder, theme, type]);

  const getRows = useCallback(
    async (params: AgGridGetRowsParams) =>
      quoteService.getCustomerQuotes({
        ...params,
        filters: {
          ...params.filters,
          archived: 0,
          corporateUsers: corporateUserIds,
          isOrder: isOrder ? 1 : 0,
        },
        pageSize: PAGE_SIZE,
        sortDir: [SortDirection.DESC],
        sortField: ['updated_at'],
      }),
    [corporateUserIds, isOrder]
  );

  return (
    <Card variant={Variant.OUTLINED} sx={{ height: CARD_HEIGHT }}>
      <CardContent sx={{ height: '100%', p: 0 }}>
        <Typography level={Level.TITLE_MD} sx={{ mt: 1, mb: 2 }}>
          {t(titleKey)}
        </Typography>
        <AgGridServerTableProvider
          tableId={tableId}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          entityType={type}
          getRows={getRows}
          hasLayoutActions={false}
          hasManageFilters={false}
          hasQuickSearch={false}
          hasRowSelection={false}
          hasStatusBar={false}
          hasTopActions={false}
          pageSize={PAGE_SIZE}
          sideBar={false}
          suppressContextMenu
        >
          <AgGridServerTable />
        </AgGridServerTableProvider>
      </CardContent>
    </Card>
  );
};
